import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  CheckSquare,
  Cpu,
  FileCheck2,
  FileText,
  FolderKanban,
  Bell,
  Kanban,
  LayoutDashboard,
  Plus,
  Search,
  Settings,
  Sparkles,
  Timer,
  Users,
  UserRound,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { useDebouncedValue, useSearch } from '@/lib/search';
import type { SearchResultData } from '@/types/api';

type Action = {
  key: string;
  label: string;
  hint?: string;
  icon: ComponentType<LucideProps>;
  run: () => void;
};

type Section = { heading: string; items: Action[] };

const KIND_ICON: Record<string, ComponentType<LucideProps>> = {
  client: Users,
  contact: UserRound,
  timeline: Activity,
};

const KIND_LABEL: Record<string, string> = {
  client: 'Clients',
  contact: 'Contacts',
  timeline: 'Timeline',
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const debounced = useDebouncedValue(q, 150);
  const { data } = useSearch(debounced);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setCursor(0);
  }, [debounced, open]);

  function close() {
    setOpen(false);
    setQ('');
  }

  function go(url: string) {
    close();
    navigate(url);
  }

  // Default sections (shown when the query is empty). Built once.
  const defaultSections = useMemo<Section[]>(
    () => [
      {
        heading: 'Go to',
        items: [
          { key: 'go-dashboard', label: 'Dashboard', icon: LayoutDashboard, run: () => go('/') },
          { key: 'go-notifications', label: 'Notifications', icon: Bell, run: () => go('/notifications') },
          { key: 'go-time', label: 'Time', icon: Timer, run: () => go('/time') },
          { key: 'go-pipeline', label: 'Pipeline', icon: Kanban, run: () => go('/leads/pipeline') },
          { key: 'go-leads', label: 'Leads', icon: Sparkles, run: () => go('/leads') },
          { key: 'go-tasks', label: 'Tasks', icon: CheckSquare, run: () => go('/leads/tasks') },
          { key: 'go-clients', label: 'Clients', icon: Users, run: () => go('/clients') },
          { key: 'go-projects', label: 'Projects', icon: FolderKanban, run: () => go('/projects') },
          { key: 'go-approvals', label: 'Approvals', icon: FileCheck2, run: () => go('/approvables') },
          { key: 'go-accounting', label: 'Accounting', icon: BookOpen, run: () => go('/accounting') },
          { key: 'go-documents', label: 'Documents', icon: FileText, run: () => go('/documents') },
          { key: 'go-assets', label: 'Assets', icon: Cpu, run: () => go('/assets') },
          { key: 'go-settings', label: 'Settings', icon: Settings, run: () => go('/settings') },
        ],
      },
      {
        heading: 'Create',
        items: [
          { key: 'new-lead', label: 'New lead', icon: Plus, run: () => go('/leads/new') },
          { key: 'new-client', label: 'New client', icon: Plus, run: () => go('/clients/new') },
          { key: 'new-project', label: 'New project', hint: 'Pick a client', icon: Plus, run: () => go('/projects') },
          { key: 'new-approvable', label: 'New approvable', hint: 'Pick a client', icon: Plus, run: () => go('/approvables') },
          { key: 'upload-document', label: 'Upload document', hint: 'Pick a client', icon: Plus, run: () => go('/documents') },
          { key: 'log-time', label: 'Log time entry', icon: Plus, run: () => go('/time') },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Filter the static sections by the query (so "new client" matches "Create › New client").
  const filteredDefaults = useMemo<Section[]>(() => {
    const term = q.trim().toLowerCase();
    if (!term) return defaultSections;
    return defaultSections
      .map((s) => ({
        heading: s.heading,
        items: s.items.filter(
          (it) =>
            it.label.toLowerCase().includes(term) ||
            (it.hint ?? '').toLowerCase().includes(term) ||
            s.heading.toLowerCase().includes(term),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [defaultSections, q]);

  // Server search results, grouped.
  const searchSections = useMemo<Section[]>(() => {
    const items = data ?? [];
    if (debounced.trim().length === 0) return [];
    const grouped = new Map<string, SearchResultData[]>();
    for (const r of items) {
      const arr = grouped.get(r.kind);
      if (arr) arr.push(r);
      else grouped.set(r.kind, [r]);
    }
    return Array.from(grouped.entries()).map(([kind, list]) => ({
      heading: KIND_LABEL[kind] ?? kind,
      items: list.map<Action>((r) => {
        const Icon = KIND_ICON[r.kind] ?? Search;
        return {
          key: `search-${r.kind}-${r.id}`,
          label: r.title,
          hint: r.subtitle ?? undefined,
          icon: Icon,
          run: () => go(r.url),
        };
      }),
    }));
  }, [data, debounced]);

  const sections = [...searchSections, ...filteredDefaults];
  const flatItems = sections.flatMap((s) => s.items);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      const hit = flatItems[cursor];
      if (hit) hit.run();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-[15vh] backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl bg-popover text-popover-foreground shadow-2xl ring-1 ring-foreground/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            autoFocus
            value={q}
            placeholder="Search clients, contacts, timeline… or jump to a page"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-1.5">
          {sections.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches.</div>
          ) : (
            sections.map((section, sIdx) => (
              <div key={`${section.heading}-${sIdx}`} className="pt-1.5 first:pt-0">
                <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.heading}
                </div>
                <ul>
                  {section.items.map((item) => {
                    const flatIndex = flatItems.indexOf(item);
                    const Icon = item.icon;
                    const active = flatIndex === cursor;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          onClick={() => item.run()}
                          onMouseEnter={() => setCursor(flatIndex)}
                          className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm ${
                            active ? 'bg-accent text-accent-foreground' : 'text-foreground'
                          }`}
                        >
                          <Icon className="size-4 text-muted-foreground" />
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.hint ? (
                            <span className="truncate text-xs text-muted-foreground">{item.hint}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-4 py-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-3">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            <span>navigate</span>
          </span>
          <span className="flex items-center gap-3">
            <Kbd>↵</Kbd>
            <span>select</span>
            <Kbd>esc</Kbd>
            <span>close</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-card px-1.5 text-[10px] font-medium text-muted-foreground">
      {children}
    </kbd>
  );
}
